import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';
import { useAdminAuth } from '../../admin/AdminAuthContext';
import { Toaster, toast } from 'sonner';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { login, isAuthed } = useAdminAuth();
  const [email, setEmail] = useState('admin@radius.app');
  const [password, setPassword] = useState('Admin123!');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  React.useEffect(() => {
    if (isAuthed) navigate('/admin', { replace: true });
  }, [isAuthed, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success('Welcome to RADIUS Admin');
      navigate('/admin', { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4" data-testid="admin-login-page">
      <Toaster position="top-center" richColors closeButton />
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/radius-icon.png" alt="RADIUS" className="w-14 h-14 rounded-2xl object-cover mb-4" data-testid="admin-login-brand-logo" />
          <h1 className="text-2xl font-black text-white tracking-tight">RADIUS Admin</h1>
          <div className="flex items-center gap-1.5 mt-2">
            <Shield className="w-3.5 h-3.5 text-pink-400" />
            <span className="text-xs text-gray-400 font-semibold">Secure admin portal</span>
          </div>
        </div>

        <form onSubmit={submit} className="bg-gray-800 rounded-2xl p-6 border border-gray-700/50 flex flex-col gap-4 brand-gradient-border">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="admin-email" className="text-xs font-bold text-gray-400">Email</label>
            <input
              id="admin-email"
              data-testid="admin-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@radius.app"
              className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="admin-password" className="text-xs font-bold text-gray-400">Password</label>
            <div className="relative">
              <input
                id="admin-password"
                data-testid="admin-login-password"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 pr-12 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-300" tabIndex={-1}>
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {err && (
            <div data-testid="admin-login-error" className="flex items-center gap-2 bg-red-500/10 text-red-400 px-3 py-2.5 rounded-xl text-xs font-semibold border border-red-500/20">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {err}
            </div>
          )}

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-brand-magenta hover:bg-pink-700 disabled:opacity-60 text-white py-3 rounded-xl font-bold text-sm transition-colors mt-1"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Sign in to Admin'}
          </button>
        </form>

        <p className="text-center text-[11px] text-gray-600 mt-4">RADIUS Admin Portal · v1.0</p>
      </div>
    </div>
  );
}
